/**
 * INSTALL — one address, scanned or typed, and an honest account of what installing does.
 *
 * Founder, 2026-08-13: "maybe add a pwa or expo build qr code like we hav ankrshield".
 *
 * ── WHO THE QR IS ACTUALLY FOR ─────────────────────────────────────────────────────────────────
 * Not the person holding the phone that is already here — they are on the app. It is for the ROOM:
 * an instructor's laptop or a projector showing this panel while twenty cadets get the address onto
 * their own phones without one of them mistyping it. That is why it lives behind a button on the
 * shell rather than on a page a candidate has to find, and why the URL is printed in full beneath the
 * code — a QR that cannot be read out loud is useless over a phone call.
 *
 * ── THE CODE IS COMPUTED AND WAS READ BACK ─────────────────────────────────────────────────────
 * It is inlined from install-qr.mjs, whose generator DECODES the symbol it produced and refuses to
 * write anything unless it reads back as the declared URL. Nothing is fetched from a QR service: an
 * external image is a blank square on ship's wifi, and it would hand the composing of our own address
 * to a third party.
 *
 * ── THREE STATES, KEPT APART ───────────────────────────────────────────────────────────────────
 * Installed · offer held · no offer. A single "Install" button that sometimes does nothing is worse
 * than no button, because a candidate concludes the app is broken rather than that their browser
 * works differently. iOS gets the actual gesture, since Safari has no install API to call.
 */
import { useEffect, useState } from 'react';
import { INSTALL_URL, INSTALL_QR_SVG } from './generated/install-qr.mjs';
import { canInstall, isInstalled, onInstallChange, promptInstall } from './offline/install-prompt.mjs';

const isApple = typeof navigator !== 'undefined'
  && /iphone|ipad|ipod/i.test(navigator.userAgent || '');

export default function InstallPanel({ onClose }) {
  const [, bump] = useState(0);
  const [outcome, setOutcome] = useState(null);
  const [copied, setCopied] = useState(false);

  // The offer can arrive after this panel is open, and installing can finish while it is open.
  useEffect(() => onInstallChange(() => bump((n) => n + 1)), []);
  useEffect(() => {
    const esc = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [onClose]);

  const installed = isInstalled();
  const offer = canInstall();

  const copy = async () => {
    try { await navigator.clipboard.writeText(INSTALL_URL); setCopied(true); setTimeout(() => setCopied(false), 2000); }
    catch { /* no clipboard permission — the address is printed right there to be typed */ }
  };

  return (
    <>
      <div className="shell-sheet-scrim" onClick={onClose} />
      <div className="inst-modal g-glass" role="dialog" aria-modal="true" aria-label="Get the app">
        <div className="inst-head">
          <div>
            <div className="g-label">One address</div>
            <h2 className="g-h2 inst-h">Get the Academy on your phone</h2>
          </div>
          <button className="g-btn g-btn-quiet inst-x" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div className="inst-body">
          {/* The plate is white in BOTH themes on purpose — dark-on-light is what every scanner
              handles, and a code that matches the theme but does not scan has failed its only job. */}
          <div className="inst-qr" dangerouslySetInnerHTML={{ __html: INSTALL_QR_SVG }} />

          <div className="inst-side">
            <p className="g-body">
              Point a phone camera at the code, or type the address. It is the same address on every
              device — there is no separate app to download and no store account needed.
            </p>
            <button className="inst-url g-btn" onClick={copy} title="Copy the address">
              <code>{INSTALL_URL}</code>
              <span className="g-dim inst-copy">{copied ? 'copied' : 'copy'}</span>
            </button>

            {installed ? (
              <p className="g-body inst-note">
                <span className="g-chip">installed</span> You are running the installed app on this
                device. Nothing further to do here.
              </p>
            ) : offer ? (
              <>
                <button className="g-btn g-btn-primary" onClick={async () => setOutcome(await promptInstall())}>
                  Install on this device
                </button>
                {outcome === 'dismissed' && (
                  <p className="g-body g-dim inst-note">
                    Not installed — you closed the browser's prompt. The app works exactly the same in
                    the browser; installing only adds the home-screen icon.
                  </p>
                )}
              </>
            ) : isApple ? (
              <p className="g-body inst-note">
                On iPhone and iPad, Safari has no install button to offer. Tap
                {' '}<b>Share</b> → <b>Add to Home Screen</b>. It then opens like an app.
                {' '}<span className="g-dim">Working without signal on iOS is not yet proven by us,
                so we do not claim it.</span>
              </p>
            ) : (
              <p className="g-body inst-note g-dim">
                This browser does not offer installation. The app runs in full from the address above —
                installing only adds a home-screen icon, so nothing is lost.
              </p>
            )}
          </div>
        </div>

        <p className="g-body g-dim inst-foot">
          Installing puts the app on the home screen. To carry the chapters, papers and drawings to sea,
          open <b>Sea readiness</b> and prepare the device while you still have signal — that is a
          separate, deliberate step, and it tells you what it actually holds.
        </p>
      </div>
    </>
  );
}
